import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Copy,
  Download,
  ListChecks,
  RotateCcw,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
} from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { FaqSection } from "@/components/seo/FaqSection";
import { TableScroll } from "@/components/layout/ContentBlock";
import { toast } from "sonner";
import { pageHead, softwareAppJsonLd, breadcrumbJsonLd, ldScript } from "@/lib/seo";
import { detectBarcode, type Candidate, type Confidence } from "@/lib/detect";

const PATH = "/batch-barcode-scanner";
const TITLE = "Batch Barcode Scanner — Validate, Dedupe, CSV Export";
const DESC =
  "Free batch barcode scanner. Paste thousands of UPC, EAN, GTIN or ISBN values, dedupe, validate check digits, detect format and export to CSV in-browser.";

const MAX_LINES = 5000;

const FAQ = [
  {
    q: "How Many Barcodes Can I Paste in One Batch?",
    a: "Up to 5,000 lines per run. The tool comfortably handles hundreds or low thousands of codes in the browser — parsing and validation are done client-side with no network round trip.",
  },
  {
    q: "What Does 'Deduplicate' Actually Do?",
    a: "After cleaning each line (stripping spaces, hyphens, dots, underscores), identical cleaned values are merged. Each duplicate gets a count so you can see how many times a code appeared in the source.",
  },
  {
    q: "How Is Each Barcode Validated?",
    a: "Numeric codes are matched against standard GTIN lengths (8, 12, 13, 14) and verified with the GS1 mod-10 check digit. ISBN-10 uses the mod-11 rule. Anything else is flagged as a free-text QR / Data Matrix payload or as unknown.",
  },
  {
    q: "What Does the CSV Export Contain?",
    a: "One row per unique cleaned barcode with columns for the original input, the cleaned value, the detected format, confidence, check-digit validity, the recommended ReadBarcode tool, and the number of duplicates removed.",
  },
  {
    q: "Can I Clean a Messy Barcode List Pasted From Excel?",
    a: "Yes — that's the main use case. Paste anything with one barcode per line (or comma-separated). The cleaner strips whitespace, hyphens and dots so codes like '978-3-16-148410-0' and '9783161484100' collapse to the same value.",
  },
  {
    q: "Does Anything Leave My Browser?",
    a: "No. All parsing, validation and CSV generation happen locally in your browser. The pasted list and the export never reach a server.",
  },
  {
    q: "Can a Phone Scan Multiple Barcodes at Once?",
    a: "Some commercial scanning SDKs decode several barcodes in a single camera frame, but the camera tools on ReadBarcode read one code per scan. In the browser, the practical route is to scan items one after another with the Inventory Scanner, export its CSV and paste the barcode column here. This page then merges the repeats and checks every code.",
  },
  {
    q: "What Is a Batch Mode on a Barcode Scanner?",
    a: "Batch mode, also called inventory or memory mode, is a setting on many cordless handheld scanners that stores reads in the device instead of sending each one to a computer straight away. You upload the stored codes later, usually as plain text. That upload is exactly the kind of list this page cleans, deduplicates and validates.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "GS1 & Inventory", path: "/gs1-inventory" },
  { name: "Batch Barcode Scanner", path: PATH },
];

export const Route = createFileRoute("/batch-barcode-scanner")({
  component: BatchBarcodePage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode Batch Barcode Scanner",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

type Row = {
  cleaned: string;
  originals: string[];
  count: number;
  best: Candidate;
  isValid: boolean | null; // null = not applicable (QR/text/unknown)
};

type Summary = {
  totalLines: number;
  nonEmpty: number;
  unique: number;
  valid: number;
  invalid: number;
  unknown: number;
};

function analyze(raw: string): { rows: Row[]; summary: Summary } {
  const lines = raw
    .split(/\r?\n|,|;|\t/)
    .map((l) => l.trim())
    .slice(0, MAX_LINES);
  const nonEmpty = lines.filter((l) => l.length > 0);

  const map = new Map<string, Row>();
  for (const original of nonEmpty) {
    const result = detectBarcode(original);
    if (result.state !== "ok") continue;
    const best = result.candidates[0];
    const cleaned = result.cleaned;
    const isValid =
      best.format === "QR / Text" || best.format === "Unknown" ? null : best.confidence === "high";
    const existing = map.get(cleaned);
    if (existing) {
      existing.count += 1;
      if (existing.originals.length < 5) existing.originals.push(original);
    } else {
      map.set(cleaned, {
        cleaned,
        originals: [original],
        count: 1,
        best,
        isValid,
      });
    }
  }

  const rows = [...map.values()];
  const summary: Summary = {
    totalLines: lines.length,
    nonEmpty: nonEmpty.length,
    unique: rows.length,
    valid: rows.filter((r) => r.isValid === true).length,
    invalid: rows.filter((r) => r.isValid === false).length,
    unknown: rows.filter((r) => r.isValid === null).length,
  };
  return { rows, summary };
}

function BatchBarcodePage() {
  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="Batch Barcode Scanner"
      description="Paste a pile of UPC, EAN, GTIN or ISBN codes. We'll clean whitespace and separators, deduplicate, run check-digit validation, detect the likely format and let you export the result to CSV — without leaving the browser. A Show only invalid filter narrows the results table to codes whose check digit fails."
      crumbs={CRUMBS}
      content={
        <>
          <ToolSeoSections
            aboutHeading="About This Batch Barcode Scanner"
            about={
              <>
                <p>
                  This batch barcode scanner takes a messy list of codes — copied from Excel, exported
                  from a POS, scraped from a supplier feed — and returns a clean, deduplicated,
                  validated list you can import anywhere. It's a fast way to convert a barcode-to-CSV
                  pipeline without writing a script.
                </p>
                <p>
                  Each line is checked against standard GTIN check digits. Codes that don't match are
                  flagged so you can fix the source rather than ship bad data downstream. For
                  single-code work, use our <Link to="/barcode-type-detector">barcode type detector</Link> or <Link to="/gtin-validator">GTIN validator</Link>
                  .
                </p>
              </>
            }
            whenToUse={[
              "Cleaning a barcode list pasted out of Excel before importing it into an ERP.",
              "Deduplicating thousands of UPC/EAN codes from a supplier catalogue.",
              "Spotting typos and bad check digits in a stocktake export.",
              "Building a CSV of validated GTINs from a print run of labels.",
              "Splitting a mixed list into ISBN, EAN-13 and free-text QR payloads before routing to the right system.",
            ]}
            howItWorks={[
              {
                title: "Paste the list",
                desc: "One barcode per line — or comma/tab-separated. We accept up to 5,000 lines per run.",
              },
              {
                title: "Clean each line",
                desc: "Spaces, hyphens, dots and underscores are stripped so '978-3-16-148410-0' becomes '9783161484100'.",
              },
              {
                title: "Deduplicate",
                desc: "Identical cleaned values collapse into one row with a duplicate count.",
              },
              {
                title: "Validate & detect",
                desc: "Each row is matched against GTIN-8/12/13/14 and ISBN-10/13 rules; the best-fit format is shown with a confidence level.",
              },
              {
                title: "Export or copy",
                desc: "Download CSV with full metadata, or copy just the cleaned barcode column for use elsewhere.",
              },
            ]}
            whatToAvoid={[
              "Pasting more than 5,000 lines at once — split very large lists into chunks for a responsive UI.",
              "Treating 'high confidence' as proof of registration. The check digit is correct, but only Verified by GS1 confirms ownership.",
              "Stripping the X from an ISBN-10 before pasting — it's the check character and removing it breaks validation.",
              "Assuming a 14-digit code is always an ITF-14 carton — some custom internal codes also use 14 digits.",
              "Re-saving the CSV in a spreadsheet that strips leading zeros — open with text import or use 'text' column format for the cleaned column.",
            ]}
            tips={[
              "Mix commas, semicolons, tabs and newlines freely — the parser splits on any of them.",
              "If you only need a deduplicated list, click 'Copy cleaned list' to get one barcode per line.",
              "Use the 'Show only invalid' filter to triage the codes that need human review.",
              "Re-running the same list is free and idempotent — paste, refine the source, paste again.",
              "For codes scanned with a camera in the field, pair this with the Inventory Scanner and export from there.",
            ]}
          />
          <h2>What Is Batch Barcode Scanning?</h2>
          <p>Batch barcode scanning means capturing many codes first and processing them together as one list, instead of handling each code the moment it is read.</p>
          <p>Many cordless handheld scanners have a batch mode that stores reads and uploads them later as text. This page does the processing half: it cleans, deduplicates and checks the whole list in one pass.</p>
          <p>It does not use a camera. The list can come from a scanner upload, a spreadsheet, a POS export or a supplier feed, with codes separated by line breaks, commas, semicolons or tabs.</p>
          <h3>Batch Scanning vs Inventory Counting</h3>
          <p>Batch scanning gives you a validated list of distinct codes, while inventory counting records how many of each item you hold and where. ReadBarcode has a tool for each, and they differ in these ways.</p>
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th scope="col">Feature</th>
                  <th scope="col">Batch Barcode Scanner</th>
                  <th scope="col">Inventory Scanner</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Input</th>
                  <td>A pasted list of codes</td>
                  <td>One code at a time, from the camera or typed</td>
                </tr>
                <tr>
                  <th scope="row">Repeated codes</th>
                  <td>Merged into one row with an occurrence count</td>
                  <td>Added to the existing item's quantity</td>
                </tr>
                <tr>
                  <th scope="row">Check digits</th>
                  <td>Checked for GTIN-8, GTIN-12, GTIN-13, GTIN-14, ISBN-10 and ISBN-13</td>
                  <td>Not checked</td>
                </tr>
                <tr>
                  <th scope="row">Extra fields</th>
                  <td>None</td>
                  <td>Product name, quantity, location and notes</td>
                </tr>
                <tr>
                  <th scope="row">Storage</th>
                  <td>Nothing is saved after you leave the page</td>
                  <td>Kept in this browser's local storage</td>
                </tr>
              </tbody>
            </table>
          </TableScroll>
          <p>Walk the shelves with the <Link to="/inventory-scanner">inventory scanner</Link> when you need a count per item. Then paste its barcode column here to catch bad check digits before the list goes into an ERP or a marketplace feed.</p>
          <h2>Scan Multiple Barcodes and Export the List</h2>
          <p>To scan multiple barcodes into one list, read them with a handheld scanner or your phone, paste the codes into the box above, and export the cleaned result as a CSV file.</p>
          <ol>
            <li>Collect the codes. A USB or Bluetooth scanner in keyboard mode types each read straight into the box, and one set to send Enter after each read puts every code on its own line.</li>
            <li>Or paste a spreadsheet column or a scanner's batch-mode upload.</li>
            <li>Press Analyze list to see the summary counts.</li>
            <li>Tick Show only invalid to review the codes whose check digit fails, and correct them at the source.</li>
            <li>Press Export CSV to download a dated CSV file, or Copy cleaned list to get one code per line.</li>
          </ol>
          <p>Multi barcode scanning in the sense of reading several symbols from one camera frame is not something this page does, since it works on text you already have. To print labels for the same list, the <Link to="/bulk-barcode-generator">bulk barcode generator</Link> turns pasted values into one barcode per row.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <BatchApp />
    </ToolLayout>
  );
}

/* -------------------- App -------------------- */

function BatchApp() {
  const [input, setInput] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [onlyInvalid, setOnlyInvalid] = useState(false);

  const { rows, summary } = useMemo(
    () => (submitted ? analyze(submitted) : { rows: [], summary: emptySummary() }),
    [submitted],
  );

  const visibleRows = useMemo(
    () => (onlyInvalid ? rows.filter((r) => r.isValid === false) : rows),
    [rows, onlyInvalid],
  );

  const onAnalyze = () => {
    const trimmed = input.trim();
    if (!trimmed) {
      toast.error("Paste a list of barcodes first.");
      return;
    }
    setSubmitted(input);
  };

  const onReset = () => {
    setInput("");
    setSubmitted("");
    setOnlyInvalid(false);
  };

  const onCopyCleaned = async () => {
    if (rows.length === 0) return;
    try {
      await navigator.clipboard.writeText(rows.map((r) => r.cleaned).join("\n"));
      toast.success(`Copied ${rows.length} cleaned barcode${rows.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  const onExport = () => {
    if (rows.length === 0) {
      toast.error("Run an analysis first.");
      return;
    }
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `barcodes-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="grid gap-6">
      <Card>
        <CardContent className="p-5 md:p-6">
          <label htmlFor="batch-input" className="text-sm font-semibold">
            Paste barcodes — one per line, or comma / tab separated
          </label>
          <Textarea
            id="batch-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              "4006381333931\n0 7640156790000\n978-3-16-148410-0\nhttps://example.com/abc\n4006381333931"
            }
            rows={8}
            className="mt-3 font-mono text-sm"
            spellCheck={false}
          />
          <p className="mt-2 text-xs text-muted-foreground">
            {input.split(/\r?\n/).filter((l) => l.trim()).length} non-empty line
            {input.split(/\r?\n/).filter((l) => l.trim()).length === 1 ? "" : "s"} · limit{" "}
            {MAX_LINES.toLocaleString()} per run.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={onAnalyze}>
              <ListChecks className="mr-2 h-4 w-4" /> Analyze list
            </Button>
            <Button variant="outline" onClick={onReset}>
              <RotateCcw className="mr-2 h-4 w-4" /> Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {submitted && (
        <Card>
          <CardContent className="p-5 md:p-6">
            <SummaryBar summary={summary} />
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={onlyInvalid}
                  onCheckedChange={(v) => setOnlyInvalid(v === true)}
                />
                Show only invalid
              </label>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={onCopyCleaned} disabled={rows.length === 0}>
                  <Copy className="mr-2 h-4 w-4" /> Copy cleaned list
                </Button>
                <Button onClick={onExport} disabled={rows.length === 0}>
                  <Download className="mr-2 h-4 w-4" /> Export CSV
                </Button>
              </div>
            </div>

            <div className="mt-4">
              {visibleRows.length === 0 ? (
                <div className="rounded-md border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
                  {rows.length === 0
                    ? "No barcodes detected in the input."
                    : "No invalid barcodes — everything looks good."}
                </div>
              ) : (
                <ResultsTable rows={visibleRows} />
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function emptySummary(): Summary {
  return { totalLines: 0, nonEmpty: 0, unique: 0, valid: 0, invalid: 0, unknown: 0 };
}

function SummaryBar({ summary }: { summary: Summary }) {
  const stats: { label: string; value: number; tone?: "ok" | "warn" | "muted" }[] = [
    { label: "Lines", value: summary.nonEmpty },
    { label: "Unique", value: summary.unique },
    { label: "Valid", value: summary.valid, tone: "ok" },
    { label: "Invalid", value: summary.invalid, tone: "warn" },
    { label: "Text / unknown", value: summary.unknown, tone: "muted" },
    { label: "Duplicates removed", value: Math.max(0, summary.nonEmpty - summary.unique) },
  ];
  return (
    <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
      {stats.map((s) => (
        <div key={s.label} className="rounded-md border border-border bg-background p-3">
          <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {s.label}
          </div>
          <div
            className={`mt-1 text-lg font-semibold ${
              s.tone === "ok"
                ? "text-emerald-600 dark:text-emerald-400"
                : s.tone === "warn"
                  ? "text-destructive"
                  : ""
            }`}
          >
            {s.value.toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
}

function ResultsTable({ rows }: { rows: Row[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">Cleaned</th>
            <th className="px-3 py-2 font-medium">Format</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium text-right">Count</th>
            <th className="px-3 py-2 font-medium">Next tool</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.cleaned} className="bg-background">
              <td className="px-3 py-2 font-mono text-xs">{r.cleaned}</td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{r.best.format}</span>
                  <ConfidenceBadge c={r.best.confidence} />
                </div>
              </td>
              <td className="px-3 py-2">
                <StatusBadge valid={r.isValid} />
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
              <td className="px-3 py-2">
                {r.best.nextTool ? (
                  <Link
                    to={r.best.nextTool.path}
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    {r.best.nextTool.label}
                  </Link>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConfidenceBadge({ c }: { c: Confidence }) {
  const map: Record<Confidence, string> = {
    high: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    medium: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    low: "bg-muted text-muted-foreground",
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${map[c]}`}
    >
      {c}
    </span>
  );
}

function StatusBadge({ valid }: { valid: boolean | null }) {
  if (valid === true)
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
        <CheckCircle2 className="h-3.5 w-3.5" /> Valid
      </span>
    );
  if (valid === false)
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive">
        <AlertTriangle className="h-3.5 w-3.5" /> Invalid
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
      <HelpCircle className="h-3.5 w-3.5" /> Text / N/A
    </span>
  );
}

/* -------------------- CSV -------------------- */

function csvEscape(value: string | number): string {
  const s = String(value ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function toCsv(rows: Row[]): string {
  const header = [
    "cleaned",
    "original_sample",
    "format",
    "confidence",
    "is_valid",
    "duplicates",
    "next_tool",
  ];
  const body = rows.map((r) =>
    [
      r.cleaned,
      r.originals[0] ?? "",
      r.best.format,
      r.best.confidence,
      r.isValid === null ? "" : r.isValid ? "true" : "false",
      r.count,
      r.best.nextTool?.path ?? "",
    ]
      .map(csvEscape)
      .join(","),
  );
  return [header.join(","), ...body].join("\n") + "\n";
}

/* -------------------- Related -------------------- */

function RelatedTools() {
  const tools: { to: string; label: string; desc: string }[] = [
    {
      to: "/barcode-type-detector",
      label: "Barcode Type Detector",
      desc: "Inspect a single code in detail with format candidates and reasons.",
    },
    {
      to: "/gtin-validator",
      label: "GTIN Validator",
      desc: "Verify a single GTIN-8/12/13/14 check digit step by step.",
    },
    {
      to: "/check-digit-calculator",
      label: "Check Digit Calculator",
      desc: "Compute the correct check digit for a partial barcode.",
    },
    {
      to: "/inventory-scanner",
      label: "Inventory Scanner",
      desc: "Build a CSV by scanning items one at a time with a camera.",
    },
    {
      to: "/isbn-barcode-reader",
      label: "ISBN Barcode Reader",
      desc: "Specialised validator for book ISBN-10 and ISBN-13 codes.",
    },
    {
      to: "/bulk-barcode-generator",
      label: "Bulk Barcode Generator",
      desc: "Turn a pasted list of values into one printable barcode per row.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-xl font-semibold tracking-tight md:text-2xl">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {tools.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            className="group rounded-lg border border-border bg-background p-4 transition hover:border-primary/50 hover:bg-muted/40"
          >
            <div className="text-sm font-semibold">{t.label}</div>
            <p className="mt-1 text-sm text-muted-foreground">{t.desc}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}
